import { CheckoutMixpanelEvent, CheckoutSuccess } from 'types/checkout';
import { getItemFromLocalStorage, setItemInLocalStorage } from '@utils/web-storage';
import { app } from '@store/modules/app';
import { buildCheckoutMixpanelProperties } from './checkout';
import { MixpanelClickData } from 'types/analytics';
import { Route } from 'vue-router';
import { VNode } from 'vue';

function hasFacebookQuery(): boolean {
	return !!window.fbq;
}

function hasGoogleAnalytics(): boolean {
	return !!window.ga;
}

function hasMixpanel(): boolean {
	return !!window.mixpanel;
}

function addProperties(properties = {} as object): object {
	return {
		...properties,
		'Page URL': window.location.pathname,
		'Sanitized Page URL': window.location.pathname.replace(/[0-9]+/g, 'X'),
		'Vue app': true
	};
}

function sanitizeNumbers(text: string | undefined): string | undefined {
	if (!text) {
		return text;
	}
	return text.replace(/([0-9][0-9,.]*[0-9]|[0-9])[kMB]?/g, 'X');
}

function getImageFileName(text: string): string {
	const matches: RegExpMatchArray | null = text.match(/[^\/]*$/g);
	if (matches) {
		return matches[0];
	}
	return text;
}

export const getSlotContent = (slot: VNode[]): string => {
	if (slot.length === 0) {
		return 'empty-slot';
	}
	return slot
		.map((vnode) => {
			if (vnode?.componentOptions?.tag === 'SvgIcon') {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const props: any = vnode.componentOptions?.propsData;
				return `Icon-${props.name}${props.dir ? '-' + props.dir : ''}`;
			}

			if (vnode?.tag === 'img') {
				if (vnode?.data?.attrs?.alt) {
					return `Img-${vnode?.data?.attrs?.alt}`;
				} else if (vnode?.data?.attrs?.src) {
					return `Img-${getImageFileName(vnode?.data?.attrs?.src)}`;
				}
			}

			if (vnode?.elm?.textContent !== null) {
				return sanitizeNumbers(vnode?.elm?.textContent);
			}
			if (vnode?.tag !== undefined) {
				return `tag-${vnode?.tag}`;
			}

			return 'unknown';
		})
		.join(' ')
		.trim();
};

function findMixpanelContext(parentElement: HTMLElement | null | undefined): string {
	if (parentElement) {
		const attr = parentElement.getAttribute('data-context');

		if (attr) {
			return attr;
		} else {
			if (parentElement.parentElement) {
				return findMixpanelContext(parentElement.parentElement);
			}
		}
	}
	return '';
}

export const getParentContext = (slot: VNode[] | undefined): string => {
	if (slot) {
		const aNode = slot[0];
		return aNode ? findMixpanelContext(aNode.elm?.parentElement) : '';
	}
	return '';
};

function getClickProperties(clickData: MixpanelClickData): Record<string, string> {
	const props: Record<string, string> = {};
	let clickText;
	let clickContext;

	if (clickData.slot) {
		clickText = clickData.clickText ?? getSlotContent(clickData.slot);
		clickContext = getParentContext(clickData.slot);
	} else {
		clickText = clickData.clickText ?? sanitizeNumbers(clickData.link?.text) ?? clickData.link?.href ?? '';
		clickContext = clickData.dataContext ?? findMixpanelContext(clickData.parentElement);
	}

	props['Click Text'] = clickText;

	let clickId = clickText;

	if (clickContext) {
		props['Click Context'] = clickContext;
		clickId = `${clickContext} : ${clickText}`;
	}

	props['Click ID'] = clickId;

	return props;
}

export const getDistinctId = (): string | undefined => {
	if (hasMixpanel() && typeof window.mixpanel.get_distinct_id == 'function') {
		return window.mixpanel.get_distinct_id();
	}
	return getItemFromLocalStorage('distinctid') || undefined;
};

export const identifyMixpanel = async (distinctid: string): Promise<void> => {
	setItemInLocalStorage('distinctid', distinctid);
	if (hasMixpanel()) {
		window.mixpanel.identify(distinctid);
		await app.initializeLaunchDarklyInstance();
	}
	if (window.gtag) {
		window.gtag({ mixpanel_id: distinctid });
	}
};

/*eslint-disable @typescript-eslint/no-explicit-any */
export const trackMixpanel = (name: string, properties = {} as any): void => {
	hasMixpanel() && window.mixpanel.track(name, addProperties(properties));
};

export const trackMixpanelBeacon = (name: string, properties = {} as any): void => {
	hasMixpanel() && window.mixpanel.track(name, addProperties(properties), { transport: 'sendBeacon' });
};

export const trackMixpanelWithCallback = (name: string, properties = {} as any, callback: () => void): void => {
	hasMixpanel() && window.mixpanel.track(name, addProperties(properties), undefined, callback);
};

export const registerMixpanel = (properties: any): void => {
	if (hasMixpanel()) {
		window.mixpanel.register(properties);
	}
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export const pageViewed = (to: Route, from: Route): void => {
	if (hasMixpanel()) {
		const params: Record<string, string> = {};
		const entries = Object.entries(to.query);

		for (const [key, val] of entries) {
			const localKey = key === 'cta' ? 'Referring CTA' : key;
			if (val) {
				params[localKey] = val.toString();
			}
		}

		const properties: Record<string, unknown> = {
			Title: to.name,
			'Browser Width': window.innerWidth,
			'Page URL': to.path,
			'Sanitized Page URL': to.path.replace(/[0-9]+/g, 'X'),
			'Vue app': true,
			...params
		};

		if (from && from.name) {
			window.mixpanel.register({
				$referrer: window.origin + process.env.VUE_APP_BASE_PATH + from.path,
				$referring_domain: window.location.host
			});

			properties['$current_url'] = `${window.location.origin}${process.env.VUE_APP_BASE_PATH}${to.fullPath}`;
		}

		window.mixpanel.track('Page Viewed', properties);
	}

	if (hasGoogleAnalytics()) {
		window.ga('send', 'pageview');
	}

	if (hasFacebookQuery()) {
		window.fbq('track', 'PageView');
	}
};

export const pageClicked = (clickData: MixpanelClickData): void => {
	if (hasMixpanel() && (clickData.slot || clickData.link || clickData.dataContext)) {
		trackMixpanel('Page Clicked', {
			...getClickProperties(clickData)
		});
	}
};

export const pageClickedBeacon = (clickData: MixpanelClickData): void => {
	if (hasMixpanel() && (clickData.slot || clickData.link || clickData.dataContext)) {
		trackMixpanelBeacon('Page Clicked', {
			Beacon: true,
			...getClickProperties(clickData)
		});
	}
};

export const trackConversionEvents = (checkoutSuccess: CheckoutSuccess): void => {
	const checkoutMixpanelProperties: CheckoutMixpanelEvent = window.fundriseApp.checkoutMixpanelProperties
		? (window.fundriseApp.checkoutMixpanelProperties as CheckoutMixpanelEvent)
		: buildCheckoutMixpanelProperties();
	checkoutMixpanelProperties['First Time Investor'] = checkoutSuccess.firstInvestmentForUser;
	if (hasMixpanel()) {
		trackMixpanel('Order Placed', checkoutMixpanelProperties);
	}
	if (window.fundriseApp.enableConversionPixels === 'true') {
		window.uetq = window.uetq || [];
		window.uetq.push('event', 'Initial Investment Placement', {
			/* eslint-disable @typescript-eslint/naming-convention */
			event_category: 'Investor',
			event_label: 'Landing Page',
			event_value: '1',
			revenue_value: checkoutSuccess.investmentAmount,
			currency: 'USD'
			/* eslint-enable @typescript-eslint/naming-convention */
		});

		if (hasGoogleAnalytics()) {
			window.ga('send', 'event', {
				eventCategory: 'Investment',
				eventAction: 'Complete',
				eventLabel: 'REIT',
				eventValue: checkoutSuccess.investmentAmount
			});

			const investmentType = checkoutSuccess.firstInvestmentForUser
				? 'First Time Investment'
				: 'Repeat Investment';
			window.ga('send', 'event', {
				eventCategory: investmentType,
				eventAction: 'Complete',
				eventLabel: 'REIT',
				eventValue: checkoutSuccess.investmentAmount
			});
		}

		if (checkoutSuccess.firstInvestmentForUser) {
			if (window.rdt) {
				window.rdt('track', 'Purchase');
			}

			if (window.pdst) {
				window.pdst('purchase', {
					value: checkoutSuccess.investmentAmount,
					currency: 'USD',
					discount_code: null,
					order_id: checkoutSuccess.userId
				});
			}
			const totalValueNumber = Number(checkoutSuccess.investmentAmount || 0);

			if (window.gtag) {
				window.gtag({
					event: 'conversion',
					value: totalValueNumber,
					currency: 'USD',
					transaction_id: checkoutSuccess.userId,
					...checkoutMixpanelProperties
				});
			}

			const totalValueWithoutOutliers = Math.min(totalValueNumber, 25000);
			if (hasFacebookQuery()) {
				window.fbq('track', 'Purchase', {
					value: totalValueWithoutOutliers,
					currency: 'USD',
					product_name: 'REIT',
					investor_type: 'new'
				});
			}

			if (window.letterpress) {
				const address = checkoutSuccess.userAddress;
				window.letterpress(
					'addTrans',
					checkoutSuccess.userId, // order ID
					'', // affiliation or store name
					checkoutSuccess.investmentAmount, // grand total
					'0', // tax amount
					'0', // shipping amount
					address ? address.city : '', // city
					address ? address.state : '', // state or province
					'USA', // country
					'USD', // currency
					[
						{
							schema: 'iglu:com.getletterpress/address/jsonschema/1-0-0',
							data: {
								name: checkoutSuccess.userName,
								street: address?.address1 ? address.address1 : '',
								street_2: address?.address2 ? address.address2 : '',
								zip: address?.zip ? address.zip : '',
								city: address?.city ? address.city : '',
								state: address?.state ? address.state : '',
								country: 'USA',
								email: checkoutSuccess.userEmail
							}
						}
					]
				);

				window.letterpress(
					'addItem',
					checkoutSuccess.userId, // order ID
					'', // SKU
					'', // product name
					'', // category or variation
					checkoutSuccess.investmentAmount, // unit price
					'1', // quantity
					'USD' // currency
				);

				window.letterpress('trackTrans');
			}
		}
	}
};

export const trackSignupEvents = (): void => {
	setItemInLocalStorage('fundrise__onWaitlist', 'true');
	if (window.fundriseApp.enableConversionPixels === 'true') {
		if (window.gtag) {
			window.gtag({
				event: 'signup',
				send_to: 'AW-844391139/CP1-CM-jwZ8BEOPF0ZID'
			});
			window.gtag({ event: 'signup' });
		}

		if (hasFacebookQuery()) {
			window.fbq('track', 'CompleteRegistration', {
				accredited: 'false'
			});
		}

		if (window.twttr) {
			window.twttr.conversion.trackPid('nvok3', { tw_sale_amount: 0, tw_order_quantity: 0 });
		}
	}

	if (hasMixpanel()) {
		trackMixpanel('Logged in');
	}
};

export const getAppleAppStoreLink = (utmCampaign: string): string => {
	return `${process.env.VUE_APP_AVATAR_URL}/app-store-redirect?utm_source=fundrise&utm_medium=website&utm_campaign=${utmCampaign}`;
};
export const getGooglePlayStoreLink = (utmCampaign: string): string => {
	return `${process.env.VUE_APP_AVATAR_URL}/play-store-redirect?utm_source=fundrise&utm_medium=website&utm_campaign=${utmCampaign}`;
};
